/**
 * ServerFailureBanner — Bandeau rouge affiché si status === 'failed'.
 *
 * Dégradation gracieuse : si `failure` shape absente du payload backend
 * (Lot 5 non livré), on masque les détails mais conserve le bandeau avec
 * les boutons par défaut (retry / rebuild / destroy / full logs).
 *
 * @spec specs/app/admin/dedicated-servers.md §6.4
 */

import { Button, Tag } from 'antd';
import { AlertTriangle, RotateCcw, ScrollText, Wrench } from 'lucide-react';
import type { FC } from 'react';
import {
  PROVISIONING_STAGE_LABELS_FR,
  type DedicatedServer,
  type RecoveryOption,
} from '@/types/dedicatedServer';

interface ServerFailureBannerProps {
  server: DedicatedServer;
  onRetryClick: () => void;
  onRebuildClick: () => void;
  onFullLogsClick: () => void;
}

const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return iso;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
};

const ServerFailureBanner: FC<ServerFailureBannerProps> = ({
  server,
  onRetryClick,
  onRebuildClick,
  onFullLogsClick,
}) => {
  const failure = server.failure ?? null;
  // Fallback si backend n'a pas encore la shape complète : recovery_options par défaut
  const recovery: RecoveryOption[] = failure?.recovery_options ?? ['retry', 'rebuild', 'destroy'];
  const canRetry = recovery.includes('retry');
  const canRebuild = recovery.includes('rebuild');

  return (
    <div className="fc-card border-red-300 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950/30">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="text-base font-bold text-red-800 dark:text-red-300">
                Provisioning échoué
              </h3>
              {failure?.occurred_at && (
                <p className="text-xs text-red-700/80 dark:text-red-400/80">
                  Survenu {formatRelative(failure.occurred_at)}
                </p>
              )}
            </div>
          </div>
        </div>

        {failure ? (
          <div className="space-y-3 text-sm text-red-900 dark:text-red-200">
            <div className="flex items-center gap-2">
              <span className="font-medium">Stage :</span>
              <Tag color="red">
                {PROVISIONING_STAGE_LABELS_FR[failure.stage] ?? failure.stage}
              </Tag>
            </div>
            <div>
              <span className="font-medium">Raison :</span>{' '}
              <span className="font-mono text-xs">{failure.reason}</span>
            </div>
            {failure.logs_tail && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wider text-red-700 dark:text-red-300">
                  Dernières lignes de logs
                </p>
                <textarea
                  readOnly
                  className="h-40 w-full rounded-md border border-red-200 bg-white p-2 font-mono text-[11px] text-red-900 dark:border-red-900 dark:bg-slate-950 dark:text-red-200"
                  value={failure.logs_tail}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-red-800 dark:text-red-200">
            Détails de l'échec indisponibles (shape `failure` absente du payload backend).
            Utilisez "Voir tous les logs" pour investiguer.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {canRetry && (
            <Button
              type="primary"
              danger
              icon={<RotateCcw className="h-4 w-4" />}
              onClick={onRetryClick}
            >
              Relancer setup
            </Button>
          )}
          {canRebuild && (
            <Button icon={<Wrench className="h-4 w-4" />} onClick={onRebuildClick}>
              Reconstruire
            </Button>
          )}
          <Button icon={<ScrollText className="h-4 w-4" />} onClick={onFullLogsClick}>
            Voir tous les logs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServerFailureBanner;
